//frontend/src/components/admin/DeliveredOrderDetails.js
// frontend/src/components/admin/DeliveredOrderDetails.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Package,
  MapPin,
  Phone,
  Calendar,
  CreditCard,
  Truck,
  User,
  RefreshCw,
  CheckCircle,
  Clock,
  ArrowLeft,
  DollarSign,
  ReceiptText, // Changed from ReceiptCheck to ReceiptText
  CalendarCheck,
  Ticket,
  AlertCircle,
  Tag
} from 'lucide-react';
import { formatWeightDisplay } from '../cart/WeightDisplay'; // Assuming this path is correct

const DeliveredOrderDetails = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/v1/admin/delivered-order/${id}`);
      if (data.success) {
        setOrder(data.order);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching order details');
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscountedPrice = (item) => {
    if (!item.discount?.active) {
      // Handle different price types
      if (item.priceType === 'perDozen') {
        return (item.dozenPrice || item.price);
      } else if (item.priceType === 'perPack') {
        return (item.packPrice || item.price);
      }
      return item.price;
    }

    // Handle base price based on price type
    let basePrice = item.price;
    if (item.priceType === 'perDozen') {
      basePrice = item.dozenPrice || item.price;
    } else if (item.priceType === 'perPack') {
      basePrice = item.packPrice || item.price;
    }

    // Apply discount
    let discountedPrice = basePrice;
    if (item.discount.type === 'percentage') {
      discountedPrice = basePrice - (basePrice * item.discount.value / 100);
    } else {
      discountedPrice = basePrice - item.discount.value;
    }

    return discountedPrice;
  };

  // Calculate the total item discount (product-level discounts)
  const calculateTotalItemDiscount = () => {
    if (!order || !order.orderItems) return 0;
   
    return order.orderItems.reduce((total, item) => {
      if (!item.discount?.active) return total;
     
      let basePrice = item.price;
      if (item.priceType === 'perDozen') {
        basePrice = item.dozenPrice || item.price;
      } else if (item.priceType === 'perPack') {
        basePrice = item.packPrice || item.price;
      }
     
      const originalPrice = basePrice * item.quantity;
      const discountedPrice = calculateDiscountedPrice(item) * item.quantity;
     
      return total + (originalPrice - discountedPrice);
    }, 0);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Using current date (April 19, 2025) information for locale formatting context
    return new Date(timestamp).toLocaleString('en-IN', { // Using Indian English locale
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true // Common preference in India
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Order not found</h3>
          <p className="mt-1 text-sm text-gray-500">The requested order could not be found.</p>
        </div>
      </div>
    );
  }

  // Calculate total discount (product discounts + coupon discount)
  const totalItemDiscount = calculateTotalItemDiscount();
  const hasCouponDiscount = order.couponDiscount && order.couponDiscount > 0;
  const totalDiscount = totalItemDiscount + (order.couponDiscount || 0);

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/admin/delivered-orders')}
            className="flex items-center text-gray-600 hover:text-[#dc2626] transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span>Back to Delivered Orders</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {order.orderStatus}
            </span>
          </div>
        </div>
        {/* Order Header */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="bg-[#dc2626] px-6 py-4 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-xl font-bold">
                  Order #{order.orderId}
                </h1>
                <p className="text-sm mt-1">
                  <span>{formatDateTime(order.createdAt)}</span>
                </p>
              </div>
              <div className="mt-2 md:mt-0">
                <div className="flex items-center gap-2 bg-white text-[#dc2626] px-3 py-1 rounded-lg shadow-sm">
                  <DollarSign className="h-4 w-4" />
                  {/* Assuming INR currency based on locale */}
                  <span className="font-bold">₹{order.totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Customer Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Name</span>
                      <p className="text-sm font-medium">{order.user.name}</p>
                    </div>
                  </div>
                  {order.user.email && (
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <span className="text-sm text-gray-500">Email</span>
                        <p className="text-sm font-medium">{order.user.email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Shipping Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Shipping Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Address</span>
                      <p className="text-sm font-medium">
                        {order.shippingInfo.address}, {order.shippingInfo.city}, {order.shippingInfo.postalCode}
                        {/* Consider adding State and Country if available */}
                        {/* {order.shippingInfo.state && `, ${order.shippingInfo.state}`} */}
                        {/* {order.shippingInfo.country && `, ${order.shippingInfo.country}`} */}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Phone</span>
                      <p className="text-sm font-medium">{order.shippingInfo.phoneno}</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Payment Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Payment Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Payment Method</span>
                      <p className="text-sm font-medium">{order.paymentInfo.paymentMethod}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    {/* Changed Icon Here */}
                    <ReceiptText className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Payment Status</span>
                      <p className="text-sm font-medium text-green-600">
                        {order.paymentInfo.status}
                      </p>
                    </div>
                  </div>
                  {order.paymentInfo.id && order.paymentInfo.id !== 'COD_PAYMENT' && (
                    <div className="flex items-start gap-2">
                      {/* Using a hash/pound symbol icon for Transaction ID */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                         <path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                       </svg>
                      <div>
                        <span className="text-sm text-gray-500">Transaction ID</span>
                        <p className="text-sm font-medium break-all">{order.paymentInfo.id}</p> {/* Added break-all for long IDs */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Delivery Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <Truck className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Delivery Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Delivered By</span>
                      <p className="text-sm font-medium">{order.deliveryStaffName || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CalendarCheck className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Delivered At</span>
                      <p className="text-sm font-medium">{formatDateTime(order.deliveredAt)}</p>
                    </div>
                  </div>
                  {order.deliveryTime && (
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <span className="text-sm text-gray-500">Preferred Delivery Time Slot</span> {/* Clarified label */}
                        <p className="text-sm font-medium">{order.deliveryTime}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Discount Information */}
            {(totalItemDiscount > 0 || hasCouponDiscount) && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Tag className="h-5 w-5 mr-1 text-[#dc2626]" />
                  Discount Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {totalItemDiscount > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Product Discounts</p>
                        <p className="text-sm text-green-600">
                          Total product discount: ₹{totalItemDiscount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                 
                  {hasCouponDiscount && (
                    <div className="flex items-start gap-2">
                      <Ticket className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Coupon Discount {order.couponCode ? `(${order.couponCode})` : ''}
                        </p>
                        <p className="text-sm text-green-600">
                          Applied coupon discount: ₹{order.couponDiscount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                 
                  {totalDiscount > 0 && (
                    <div className="pt-2 border-t">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Total Discount</p>
                          <p className="text-sm text-green-600">
                            Customer saved a total of ₹{totalDiscount.toFixed(2)} on this order
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Order Items */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Package className="h-5 w-5 mr-1 text-[#dc2626]" />
                Order Items
              </h3>
              <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200"> {/* Added border */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price Type / Weight
                        </th>
                        <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"> {/* Centered */}
                          Quantity
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"> {/* Right aligned */}
                          Unit Price
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"> {/* Right aligned */}
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {order.orderItems.map((item, index) => (
                        <tr key={item._id || index} className="hover:bg-gray-50"> {/* Use item._id if available */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <img className="h-10 w-10 rounded-md object-cover border border-gray-200" src={item.image} alt={item.name} /> {/* Changed to rounded-md and added border */}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                {/* Optionally add product ID or SKU here */}
                                {/* <div className="text-xs text-gray-500">ID: {item.product}</div> */}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {item.priceType === 'perKg' && 'Per Kg'}
                            {item.priceType === 'perDozen' && 'Per Dozen'}
                            {item.priceType === 'perPack' && 'Per Pack'}
                            {item.priceType === 'fixed' && (item.selectedWeight || 'Fixed Weight')}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center"> {/* Centered */}
                            {formatWeightDisplay(item)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right"> {/* Right aligned */}
                            {item.discount?.active ? (
                              <div className="flex flex-col items-end"> {/* Align text right */}
                                <span className="text-xs text-gray-500 line-through">
                                  ₹{item.priceType === 'perDozen'
                                    ? (item.dozenPrice || item.price).toFixed(2)
                                    : item.priceType === 'perPack'
                                    ? (item.packPrice || item.price).toFixed(2)
                                    : item.price.toFixed(2)}
                                </span>
                                <span className="text-sm text-red-600">
                                  ₹{calculateDiscountedPrice(item).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm">
                                ₹{item.priceType === 'perDozen'
                                  ? (item.dozenPrice || item.price).toFixed(2)
                                  : item.priceType === 'perPack'
                                  ? (item.packPrice || item.price).toFixed(2)
                                  : item.price.toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#dc2626] text-right"> {/* Right aligned */}
                            ₹{(calculateDiscountedPrice(item) * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100"> {/* Changed background */}
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium text-gray-700">Items Subtotal:</td> {/* Changed text color */}
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">₹{order.itemsPrice.toFixed(2)}</td> {/* Changed text color */}
                      </tr>
                      {/* Show product level discount total if any */}
                      {totalItemDiscount > 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium text-green-600">Item Discounts:</td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">-₹{totalItemDiscount.toFixed(2)}</td>
                        </tr>
                      )}
                      {/* Show coupon discount if applied */}
                      {hasCouponDiscount && (
                        <tr>
                          <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium text-green-600">
                            Coupon Discount {order.couponCode ? `(${order.couponCode})` : ''}:
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">-₹{order.couponDiscount.toFixed(2)}</td>
                        </tr>
                      )}
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium text-gray-700">Shipping Charge:</td> {/* Changed text color */}
                        <td className="px-4 py-3 text-sm font-medium text-right">
                          {order.shippingPrice > 0
                            ? `₹${order.shippingPrice.toFixed(2)}`
                            : <span className="text-green-600">FREE</span>}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-right font-semibold text-gray-900">Total Amount:</td> {/* Changed text color */}
                        <td className="px-4 py-3 text-sm font-bold text-[#dc2626] text-right">₹{order.totalPrice.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            {/* Order Timeline */}
            <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Clock className="h-5 w-5 mr-1 text-[#dc2626]" />
                Order Timeline
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"> {/* Added border */}
                {/* Consider making this a more visually appealing timeline if needed */}
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3 mt-0.5 bg-green-100 rounded-full p-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Order Placed</p>
                      <p className="text-xs text-gray-500">{formatDateTime(order.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                     <div className="flex-shrink-0 mr-3 mt-0.5 bg-yellow-100 rounded-full p-1">
                       <Package className="h-4 w-4 text-yellow-600" />
                     </div>
                     <div>
                       <p className="text-sm font-medium">Order Packed</p>
                       <p className="text-xs text-gray-500">
                         {order.statusUpdates?.packed ? formatDateTime(order.statusUpdates.packed) : 'N/A'} {/* Handle missing status */}
                       </p>
                     </div>
                   </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3 mt-0.5 bg-blue-100 rounded-full p-1">
                      <Truck className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Out for Delivery</p>
                      <p className="text-xs text-gray-500">
                        {order.statusUpdates?.outForDelivery ? formatDateTime(order.statusUpdates.outForDelivery) : 'N/A'} {/* Handle missing status */}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3 mt-0.5 bg-green-100 rounded-full p-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Delivered</p>
                      <p className="text-xs text-gray-500">{formatDateTime(order.deliveredAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveredOrderDetails;